import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type {
  Product,
  ProductSkuPreview,
  SkuPreviewState,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { ScrollArea } from '@nucleus-commerce/ui/components/scroll-area';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useRegenerateSkus } from '../hooks/use-regenerate-skus';
import { useSkuPreview } from '../hooks/use-sku-preview';

import { skuInUseWords, skuProblemWords } from './sku-words';

export interface RegenerateSkusDialogProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The root row shares the variant rows' shape under this id. */
const ROOT_ID = '__root__';

interface PlanRow {
  id: string;
  title: string;
  current: string | null;
  next: string | null;
  isManual: boolean;
  state: SkuPreviewState;
  /** Second line under the title: in-use reasons, the counter note, the conflict note. */
  note: string | null;
}

/** Ticked unless told otherwise: what the rule changes, and manual values once they are included. */
const defaultChecked = (row: PlanRow, includeManual: boolean) =>
  row.state === 'change' || row.state === 'conflict' || (row.state === 'manual' && includeManual);

/** In-use, unchanged and empty rows have nothing to write; manual ones only with the box ticked. */
const canToggle = (row: PlanRow, includeManual: boolean) =>
  row.state === 'change' || row.state === 'conflict' || (row.state === 'manual' && includeManual);

function StateChip({ state }: { state: SkuPreviewState }) {
  const { t } = useTranslation('products');
  const variant =
    state === 'change' || state === 'conflict'
      ? 'default'
      : state === 'manual'
        ? 'secondary'
        : 'outline';
  return (
    <Badge
      variant={variant}
      data-testid={`sku-state-${state}`}
      className={cn('font-normal', state === 'conflict' && 'bg-amber-600 text-white')}
    >
      {t(`sku.state.${state}`)}
    </Badge>
  );
}

function DialogBody({ product, onClose }: { product: Product; onClose: () => void }) {
  const { t } = useTranslation(['products', 'common']);
  const message = useErrorMessage();
  const [includeManual, setIncludeManual] = useState(false);
  // Only the person's own toggles are kept; everything else follows the preview's defaults, so a
  // reload (the include-manual box) resets the ticks without an effect.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const preview = useSkuPreview(product.id, includeManual);
  const regenerate = useRegenerateSkus(product.id);

  const rows = preview.data ? toRows(preview.data, t) : [];
  const checked = (row: PlanRow) =>
    canToggle(row, includeManual) && (overrides[row.id] ?? defaultChecked(row, includeManual));
  const chosen = rows.filter(checked);
  const rootChosen = chosen.some((row) => row.id === ROOT_ID);
  const variantIds = chosen.filter((row) => row.id !== ROOT_ID).map((row) => row.id);

  const apply = () =>
    regenerate.mutate(
      { root: rootChosen, variantIds, includeManual },
      { onSuccess: () => onClose() },
    );

  return (
    <>
      <div className="flex flex-col gap-3 border-b px-6 py-4">
        <p className="text-muted-foreground text-sm">
          {preview.data
            ? preview.data.rule
              ? t('products:sku.dialog.rule', { rule: preview.data.rule.name })
              : t('products:sku.dialog.noRule')
            : null}
        </p>
        <Alert>
          <AlertDescription>{t('products:sku.dialog.inUseNote')}</AlertDescription>
        </Alert>
        <div className="flex items-start gap-2.5">
          <Checkbox
            id="sku-include-manual"
            className="mt-0.5"
            checked={includeManual}
            onCheckedChange={(value) => {
              setIncludeManual(value === true);
              setOverrides({});
            }}
          />
          <div className="grid gap-0.5">
            <Label htmlFor="sku-include-manual">{t('products:sku.dialog.includeManual')}</Label>
            <span className="text-muted-foreground text-xs">
              {t('products:sku.dialog.includeManualHint')}
            </span>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
        {preview.isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : preview.error ? (
          <ErrorState error={preview.error} onRetry={() => void preview.refetch()} compact />
        ) : (
          // Radix wraps the viewport's content in a `display: table` box that grows with its widest
          // child; `block` keeps the table at the dialog's width so long SKUs wrap instead.
          <ScrollArea className="min-h-0 flex-1 overflow-hidden rounded-lg border [&_[data-slot=scroll-area-viewport]>div]:!block">
            <table
              aria-busy={preview.isFetching}
              className="w-full table-fixed caption-bottom text-sm"
            >
              <TableHeader className="bg-background sticky top-0 z-10 shadow-[inset_0_-1px_0_var(--color-border)] [&_tr]:border-b-0">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10" />
                  <TableHead className="w-[30%]">{t('products:sku.dialog.columns.row')}</TableHead>
                  <TableHead>{t('products:sku.dialog.columns.current')}</TableHead>
                  <TableHead className="w-6" />
                  <TableHead>{t('products:sku.dialog.columns.next')}</TableHead>
                  <TableHead className="w-28">{t('products:sku.dialog.columns.state')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const enabled = canToggle(row, includeManual);
                  const greyed = row.state === 'inUse';
                  return (
                    <TableRow
                      key={row.id}
                      aria-label={row.title}
                      className={cn(greyed && 'text-muted-foreground opacity-70')}
                    >
                      <TableCell className="pl-3">
                        <Checkbox
                          aria-label={t('products:sku.dialog.select', { title: row.title })}
                          checked={checked(row)}
                          disabled={!enabled}
                          onCheckedChange={(value) =>
                            setOverrides((prev) => ({ ...prev, [row.id]: value === true }))
                          }
                        />
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <span className="block font-medium break-words">{row.title}</span>
                        {row.note ? <span className="block text-xs">{row.note}</span> : null}
                      </TableCell>
                      <TableCell className="font-mono text-xs break-all whitespace-normal">
                        {row.current ?? '—'}
                      </TableCell>
                      <TableCell className="px-0">
                        <ArrowRight aria-hidden className="text-muted-foreground size-3.5" />
                      </TableCell>
                      <TableCell className="font-mono text-xs break-all whitespace-normal">
                        {row.next ?? '—'}
                      </TableCell>
                      <TableCell>
                        <StateChip state={row.state} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </table>
          </ScrollArea>
        )}

        {regenerate.error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(regenerate.error)}</AlertDescription>
          </Alert>
        ) : null}
      </div>
      <DialogFooter className="border-t px-6 py-4">
        <Button type="button" variant="ghost" onClick={onClose} disabled={regenerate.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="button"
          disabled={chosen.length === 0 || regenerate.isPending || preview.isFetching}
          onClick={apply}
        >
          {regenerate.isPending
            ? t('products:sku.dialog.working')
            : t('products:sku.dialog.submit', { count: chosen.length })}
        </Button>
      </DialogFooter>
    </>
  );
}

function toRows(
  preview: ProductSkuPreview,
  t: ReturnType<typeof useTranslation<['products', 'common']>>['t'],
): PlanRow[] {
  const root = preview.root;
  const rootNote = root.problem
    ? t('products:sku.dialog.problem', { reason: skuProblemWords(t, root.problem) })
    : root.allocates && root.state !== 'none'
      ? t('products:sku.dialog.allocates')
      : null;
  const rootRow: PlanRow = {
    id: ROOT_ID,
    title: t('products:sku.dialog.root'),
    current: root.current,
    next: root.next,
    isManual: root.isManual,
    state: root.state,
    note: rootNote,
  };
  const variantRows = preview.variants.map((variant): PlanRow => ({
    id: variant.id,
    title: variant.title,
    current: variant.current,
    next: variant.next,
    isManual: variant.isManual,
    state: variant.state,
    note:
      variant.state === 'inUse'
        ? skuInUseWords(t, variant.inUseReason)
        : variant.state === 'conflict'
          ? t('products:sku.dialog.conflictNote')
          : null,
  }));
  return [rootRow, ...variantRows];
}

/**
 * Current → next for the style number and every variant, from the rule. Variants that orders,
 * carts or stock refer to are never rewritten; manual values only when asked.
 */
export function RegenerateSkusDialog({ product, open, onOpenChange }: RegenerateSkusDialogProps) {
  const { t } = useTranslation('products');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(85vh,48rem)] flex-col gap-0 p-0 sm:max-w-4xl">
        <DialogHeader className="border-b px-6 pt-6 pb-4">
          <DialogTitle>{t('sku.dialog.title')}</DialogTitle>
          <DialogDescription>{t('sku.dialog.description')}</DialogDescription>
        </DialogHeader>
        {open ? <DialogBody product={product} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
